import { useState, useEffect, useCallback, useRef } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../../lib/api';
import { PlatformIcon } from '../primitives';
import styles from './SpotlightSearch.module.css';

const POSTER_CDN = 'https://cdn-fastly.parrotanalytics.com/images/poster-hyphen';

interface TitleResult {
  parrot_id: string;
  title: string;
  content_type?: string;
  main_genre?: string;
  release_year?: number;
  availability_platform?: string;
}

interface PlatformResult {
  platform: string;
  subscribers?: number;
}

interface SearchResults {
  titles: TitleResult[];
  platforms: PlatformResult[];
}

const EMPTY: SearchResults = { titles: [], platforms: [] };

/** Titles come back one row per title×platform; collapse to one entry per id. */
function dedupeTitles(rows: TitleResult[]): TitleResult[] {
  const seen = new Map<string, TitleResult>();
  for (const r of rows) {
    if (!seen.has(r.parrot_id)) seen.set(r.parrot_id, r);
  }
  return [...seen.values()];
}

/** Platforms are a small fixed set; fetched once and filtered in the browser. */
let platformCache: PlatformResult[] | null = null;
async function loadPlatforms(): Promise<PlatformResult[]> {
  if (platformCache) return platformCache;
  const res = await fetch(apiUrl('/platforms/leaderboard'));
  if (!res.ok) throw new Error('platforms');
  const body: { data: PlatformResult[] } = await res.json();
  platformCache = body.data ?? [];
  return platformCache;
}

async function fetchResults(q: string): Promise<SearchResults> {
  const [titleRows, platforms] = await Promise.all([
    fetch(apiUrl(`/leaderboard?search=${encodeURIComponent(q)}&timePeriod=all&limit=8`))
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((b: { data: TitleResult[] }) => dedupeTitles(b.data ?? []).slice(0, 6)),
    loadPlatforms().catch(() => [] as PlatformResult[]),
  ]);

  const lower = q.toLowerCase();
  const matchedPlatforms = platforms
    .filter((p) => p.platform.toLowerCase().includes(lower))
    .slice(0, 4);

  return { titles: titleRows, platforms: matchedPlatforms };
}

function hl(text: string, q: string): React.ReactNode {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className={styles.mark}>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

function titleSub(t: TitleResult): string {
  const parts = [
    t.content_type === 'movie' ? 'Movie' : 'Show',
    t.main_genre ? t.main_genre.charAt(0).toUpperCase() + t.main_genre.slice(1) : undefined,
    t.release_year ? String(t.release_year) : undefined,
  ].filter(Boolean);
  return parts.join(' · ');
}

export interface SpotlightSearchProps {
  open: boolean;
  onClose: () => void;
}

export function SpotlightSearch({ open, onClose }: SpotlightSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<HTMLButtonElement[]>([]);
  const navigate = useNavigate();

  /* Monotonic id for each search. A response only wins if it belongs to the
     most recent search — so a slow earlier request (e.g. "hazb") can't overwrite
     the results of a later one ("hazbin") when it resolves out of order. */
  const searchSeq = useRef(0);

  const runSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      searchSeq.current++; // invalidate any in-flight response
      setResults(EMPTY);
      setLoading(false);
      setActiveIdx(-1);
      return;
    }
    const seq = ++searchSeq.current;
    setLoading(true);
    try {
      const res = await fetchResults(q);
      if (seq !== searchSeq.current) return; // a newer search superseded this one
      setResults(res);
      setActiveIdx(-1);
    } catch {
      if (seq !== searchSeq.current) return;
      setResults(EMPTY);
      setActiveIdx(-1);
    } finally {
      if (seq === searchSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(EMPTY);
      setActiveIdx(-1);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const allItems = [
    ...results.titles.map((t) => ({ type: 'title' as const, data: t })),
    ...results.platforms.map((p) => ({ type: 'platform' as const, data: p })),
  ];

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, allItems.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter' && activeIdx >= 0 && allItems[activeIdx]) {
      e.preventDefault();
      itemsRef.current[activeIdx]?.click();
    }
  }

  function goTitle(id: string) {
    onClose();
    navigate(`/titles/${id}`);
  }
  function goPlatform(name: string) {
    onClose();
    navigate(`/platforms/${encodeURIComponent(name)}`);
  }

  useEffect(() => {
    if (activeIdx >= 0 && itemsRef.current[activeIdx]) {
      itemsRef.current[activeIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  const hasResults = allItems.length > 0;
  const showEmpty = query.length >= 2 && !hasResults && !loading;

  if (!open) return null;

  let flatIdx = -1;

  return (
    <div
      className={`${styles.overlay} ${styles.overlayActive}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={handleKeyDown}
    >
      <div className={`${styles.card} ${styles.cardVisible}`}>
        <div className={`${styles.bar} ${loading ? styles.barSearching : ''}`}>
          {loading && (
            <>
              <span className={styles.glowBorder} aria-hidden />
              <span className={styles.glowOuter} aria-hidden />
              <span className={styles.glowCover} aria-hidden />
            </>
          )}
          <div className={styles.barContent}>
            <Search size={20} className={styles.barIcon} />
            <input
              ref={inputRef}
              type={'text'}
              className={styles.barInput}
              placeholder={'Search titles, platforms…'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <kbd className={styles.kbd}>{'ESC'}</kbd>
          </div>
        </div>

        {(hasResults || showEmpty) && (
          <div className={styles.results}>
            {showEmpty && <div className={styles.empty}>{`No results for "${query}"`}</div>}

            {results.titles.length > 0 && (
              <>
                <div className={styles.groupLabel}>{'Titles'}</div>
                {results.titles.map((t) => {
                  flatIdx++;
                  const idx = flatIdx;
                  return (
                    <button
                      key={t.parrot_id}
                      ref={(el) => {
                        if (el) itemsRef.current[idx] = el;
                      }}
                      type={'button'}
                      className={`${styles.item} ${activeIdx === idx ? styles.itemActive : ''}`}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => goTitle(t.parrot_id)}
                    >
                      <img
                        src={`${POSTER_CDN}/${t.parrot_id}.jpg?width=48`}
                        alt={''}
                        className={styles.itemPoster}
                        loading={'lazy'}
                        onError={(e) => {
                          e.currentTarget.style.visibility = 'hidden';
                        }}
                      />
                      <div className={styles.itemText}>
                        <div className={styles.itemName}>{hl(t.title, query)}</div>
                        <div className={styles.itemSub}>{titleSub(t)}</div>
                      </div>
                    </button>
                  );
                })}
              </>
            )}

            {results.platforms.length > 0 && (
              <>
                <div className={styles.groupLabel}>{'Platforms'}</div>
                {results.platforms.map((p) => {
                  flatIdx++;
                  const idx = flatIdx;
                  return (
                    <button
                      key={p.platform}
                      ref={(el) => {
                        if (el) itemsRef.current[idx] = el;
                      }}
                      type={'button'}
                      className={`${styles.item} ${activeIdx === idx ? styles.itemActive : ''}`}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => goPlatform(p.platform)}
                    >
                      <span className={styles.itemLogoWrap}>
                        <PlatformIcon name={p.platform} size={24} />
                      </span>
                      <div className={styles.itemText}>
                        <div className={styles.itemName}>{hl(p.platform, query)}</div>
                        <div className={styles.itemSub}>{'Platform'}</div>
                      </div>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
